import React from 'react';
import type { CSSObject } from '@emotion/react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { View } from '@amzn/stencil-react-components/layout';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

interface AstraMarkdownProps {
  readonly content: string;
}

/** Test id on the markdown root. */
export const ASTRA_MARKDOWN_TEST_ID = 'astra-markdown';

const BODY_SIZE = token('text.size.200');
const BODY_LINE_HEIGHT = token('text.lineHeight.200');
const BOLD = token('text.weight.bold');
const BLOCK_MARGIN = `${token('dimensions.spacing.200')} 0`;
const HEADING_MARGIN = `${token('dimensions.spacing.200')} 0 ${token('dimensions.spacing.100')}`;
const INLINE_BORDER = `${token('dimensions.border.width.100')} solid ${token('color.border.primary')}`;
/** Opens deep-links in a new tab without handing the opened page `window.opener` or the referrer. */
const NEW_TAB_REL = 'noopener noreferrer';

/**
 * Prose styling for an agent answer, expressed entirely in Stencil design
 * tokens so it tracks the theme the surrounding `AgentChatPanel` renders in.
 *
 * Standard markdown emits h1–h3; those are stepped down to the body-adjacent
 * sizes the narrow chat column can hold. Block margins collapse at the start
 * and end of the answer so the bubble's own padding frames the content.
 */
const PROSE_STYLE: CSSObject = {
  'fontFamily': token('text.family.default'),
  'fontSize': BODY_SIZE,
  'lineHeight': BODY_LINE_HEIGHT,
  'color': token('color.text.primary'),
  'overflowWrap': 'anywhere',
  '& > :first-child': { marginTop: 0 },
  '& > :last-child': { marginBottom: 0 },
  '& p': { margin: BLOCK_MARGIN },
  '& h1': {
    fontSize: token('text.size.400'),
    lineHeight: token('text.lineHeight.400'),
    fontWeight: BOLD,
    margin: HEADING_MARGIN,
  },
  '& h2': {
    fontSize: token('text.size.300'),
    lineHeight: token('text.lineHeight.300'),
    fontWeight: BOLD,
    margin: HEADING_MARGIN,
  },
  '& h3, & h4, & h5, & h6': {
    fontSize: BODY_SIZE,
    lineHeight: BODY_LINE_HEIGHT,
    fontWeight: BOLD,
    margin: HEADING_MARGIN,
  },
  '& ul, & ol': {
    margin: BLOCK_MARGIN,
    paddingInlineStart: token('dimensions.spacing.500'),
  },
  '& li': { margin: `${token('dimensions.spacing.100')} 0` },
  '& li > p': { margin: 0 },
  '& a': {
    color: token('color.text.accent-on-bg-default'),
    textDecoration: 'underline',
  },
  '& code': {
    fontFamily: token('text.family.modernText'),
    fontSize: token('text.size.100'),
    backgroundColor: token('color.fill.neutral.muted'),
    borderRadius: token('dimensions.border.radius.100'),
    padding: `0 ${token('dimensions.spacing.100')}`,
  },
  '& pre': {
    margin: BLOCK_MARGIN,
    padding: token('dimensions.spacing.300'),
    backgroundColor: token('color.fill.neutral.muted'),
    borderRadius: token('dimensions.border.radius.200'),
    overflowX: 'auto',
  },
  '& pre code': { padding: 0, backgroundColor: 'transparent' },
  '& blockquote': {
    margin: BLOCK_MARGIN,
    paddingInlineStart: token('dimensions.spacing.300'),
    borderInlineStart: `${token('dimensions.border.width.200')} solid ${token('color.fill.neutral.secondary')}`,
    color: token('color.text.secondary'),
  },
  '& table': {
    margin: BLOCK_MARGIN,
    borderCollapse: 'collapse',
    width: '100%',
  },
  '& th, & td': {
    border: INLINE_BORDER,
    padding: `${token('dimensions.spacing.100')} ${token('dimensions.spacing.200')}`,
    textAlign: 'start',
    verticalAlign: 'top',
  },
  '& th': {
    fontWeight: BOLD,
    backgroundColor: token('color.surface.bg-muted'),
  },
  '& hr': {
    border: 0,
    borderTop: INLINE_BORDER,
    margin: `${token('dimensions.spacing.300')} 0`,
  },
};

/**
 * Open links in a new tab so clicking an entity deep-link (audit, task,
 * question, …) doesn't navigate away from — and discard — the conversation.
 * A `noopener` rel is required with `target="_blank"` to keep the opened page
 * off `window.opener` (reverse tabnabbing); `noreferrer` drops the referrer.
 * react-markdown already sanitizes the href (relative and http/https/mailto/
 * tel kept; javascript: and other schemes dropped), so only link behaviour is
 * added here — no raw HTML is ever rendered.
 *
 * Only `href` and `title` are forwarded: spreading react-markdown's `node`
 * (hast AST) prop onto the anchor would trigger an unknown-DOM-prop warning.
 */
const markdownComponents: Components = {
  a: ({ children, href, title }) => (
    <a href={href} title={title} target="_blank" rel={NEW_TAB_REL}>
      {children}
    </a>
  ),
};

/**
 * Renders an Astra answer written in markdown, styled with Stencil tokens.
 *
 * Uses `react-markdown`, which parses markdown into a React element tree —
 * there is no `dangerouslySetInnerHTML` and no HTML-string injection. Raw
 * HTML embedded in the markdown is not rendered as markup (no `rehype-raw`),
 * so a model cannot emit active content. `remark-gfm` adds GitHub-flavoured
 * markdown (tables, task lists, strikethrough, autolinks).
 *
 * Plugged into the shared `AgentChatPanel` through its `markdownRenderer`
 * prop by `useAstraPanelProps`.
 */
export function AstraMarkdown({ content }: AstraMarkdownProps): React.JSX.Element {
  return (
    <View css={PROSE_STYLE} dataTestId={ASTRA_MARKDOWN_TEST_ID}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </View>
  );
}
